import { isAfter, parseISO } from "date-fns";

import type {
	ShipmentCustomer,
	ShipmentDetailsData,
} from "~/shared/schemas/shipment-management-schema.ts";

// Accessorial that makes a commodity piece count change relevant to rating.
const SORT_AND_SEGREGATE = "Sort and Segregate";

type Stop = ShipmentDetailsData["pickup"];
type FreightDetails = ShipmentDetailsData["freightDetails"];
type LineItem = FreightDetails["lineItems"][number];

const parseDate = (value?: string | null): Date | null => {
	if (!value) {
		return null;
	}
	const parsed = parseISO(value);
	return Number.isNaN(parsed.getTime()) ? null : parsed;
};

// Order-insensitive comparison of two accessorial name lists.
const accessorialsChanged = (
	original?: readonly string[] | null,
	edited?: readonly string[] | null,
): boolean => {
	const originalSet = new Set(original ?? []);
	const editedSet = new Set(edited ?? []);
	if (originalSet.size !== editedSet.size) {
		return true;
	}
	for (const value of originalSet) {
		if (!editedSet.has(value)) {
			return true;
		}
	}
	return false;
};

export const sortAndSegregateSelected = (
	details: ShipmentDetailsData | undefined,
): boolean =>
	(details?.delivery?.accessorials ?? []).includes(SORT_AND_SEGREGATE) ||
	(details?.pickup?.accessorials ?? []).includes(SORT_AND_SEGREGATE);

export const hasInvalidPieceCountsForSortAndSegregate = (
	details: ShipmentDetailsData | undefined,
): boolean => {
	if (!sortAndSegregateSelected(details)) {
		return false;
	}

	return (details?.freightDetails?.lineItems ?? []).some((lineItem) => {
		const pieceCount = Number(lineItem.pieceCount);
		return !Number.isFinite(pieceCount) || pieceCount <= 0;
	});
};

// Per the rerate matrix, a stop forces a rerate when its company name, address,
// location type or accessorials change. Contact info, hours, scheduled, actual
// and the stop number do not trigger a rerate.
const stopRequiresRerate = (original?: Stop, edited?: Stop): boolean =>
	original?.companyName !== edited?.companyName ||
	original?.address !== edited?.address ||
	original?.locationType !== edited?.locationType ||
	accessorialsChanged(original?.accessorials, edited?.accessorials);

// Returns null when billing matches the tendered/root customer (no distinct
// billing selection), so API-stored self-billing and a null draft compare equal.
const effectiveBillingId = (
	customer: ShipmentCustomer | null | undefined,
): number | null | undefined => {
	if (!customer) return undefined;
	const tenderedId = customer.tenderedCustomer?.customerId ?? customer.id;
	const billingId = customer.billingCustomer?.customerId;
	return billingId == null || billingId === tenderedId ? null : billingId;
};

// Customer edits that affect rating: tendered customer and billing customer.
const customerRequiresRerate = (
	original?: ShipmentCustomer | null,
	edited?: ShipmentCustomer | null,
): boolean =>
	original?.id !== edited?.id ||
	effectiveBillingId(original) !== effectiveBillingId(edited);

// Commodity fields that force a rerate when changed (handling unit, weight,
// dimensions, stackable, hazmat, description, class, NMFC). Unit and the
// auto-generated estimated class do not. Piece count only matters when the
// "Sort and Segregate" accessorial is selected. Overwrite calculated linear
// feet also forces a rerate.
const lineItemRequiresRerate = (
	original: LineItem,
	edited: LineItem,
	includePieceCount: boolean,
): boolean =>
	original.handlingUnit !== edited.handlingUnit ||
	original.totalWeight !== edited.totalWeight ||
	original.length !== edited.length ||
	original.width !== edited.width ||
	original.height !== edited.height ||
	original.stackable !== edited.stackable ||
	original.hazmat !== edited.hazmat ||
	original.commodityDescription !== edited.commodityDescription ||
	original.class !== edited.class ||
	original.nmfc !== edited.nmfc ||
	(includePieceCount && original.pieceCount !== edited.pieceCount);

const freightRequiresRerate = (
	original: FreightDetails | undefined,
	edited: FreightDetails | undefined,
	includePieceCount: boolean,
): boolean => {
	const originalItems = original?.lineItems ?? [];
	const editedItems = edited?.lineItems ?? [];
	if (originalItems.length !== editedItems.length) {
		return true;
	}
	return originalItems.some((item, index) => {
		const editedItem = editedItems[index];
		return editedItem
			? lineItemRequiresRerate(item, editedItem, includePieceCount)
			: true;
	});
};

const freightDetailsRequiresRerate = (
	original: FreightDetails | undefined,
	edited: FreightDetails | undefined,
	includePieceCount: boolean,
): boolean => {
	if (!original || !edited) {
		return false;
	}

	const originalLinearFeet =
		original.overwriteCalculatedLinearFeet ?? original.calculatedLinearFeet;
	const editedLinearFeet =
		edited.overwriteCalculatedLinearFeet ?? edited.calculatedLinearFeet;

	return (
		freightRequiresRerate(original, edited, includePieceCount) ||
		originalLinearFeet !== editedLinearFeet
	);
};

export const areRerateRelevantDetailsEqual = (
	left: ShipmentDetailsData | null,
	right: ShipmentDetailsData | null,
): boolean => {
	if (left === right) {
		return true;
	}
	if (!left || !right) {
		return false;
	}

	return (
		!stopRequiresRerate(left.pickup, right.pickup) &&
		!stopRequiresRerate(left.delivery, right.delivery) &&
		left.pickup?.scheduled === right.pickup?.scheduled &&
		!freightDetailsRequiresRerate(
			left.freightDetails,
			right.freightDetails,
			true,
		)
	);
};

type AdditionalDetails = ShipmentDetailsData["additionalDetails"];

const contactRequiresDocGen = (
	original: Stop["contact"] | undefined,
	edited: Stop["contact"] | undefined,
): boolean =>
	original?.name !== edited?.name ||
	original?.phone !== edited?.phone ||
	original?.extension !== edited?.extension;

// Pickup includes scheduled; delivery excludes scheduled and actual.
const stopRequiresDocGen = (
	original: Stop | undefined,
	edited: Stop | undefined,
	includeScheduled: boolean,
): boolean =>
	original?.companyName !== edited?.companyName ||
	original?.address !== edited?.address ||
	original?.address1 !== edited?.address1 ||
	original?.address2 !== edited?.address2 ||
	original?.city !== edited?.city ||
	original?.stateProvince !== edited?.stateProvince ||
	original?.country !== edited?.country ||
	original?.postalCode !== edited?.postalCode ||
	original?.locationType !== edited?.locationType ||
	accessorialsChanged(original?.accessorials, edited?.accessorials) ||
	contactRequiresDocGen(original?.contact, edited?.contact) ||
	original?.shipmentHoursFrom !== edited?.shipmentHoursFrom ||
	original?.shipmentHoursTo !== edited?.shipmentHoursTo ||
	original?.appointmentType !== edited?.appointmentType ||
	(includeScheduled && original?.scheduled !== edited?.scheduled) ||
	original?.shipmentNumber !== edited?.shipmentNumber ||
	original?.specialInstructions !== edited?.specialInstructions;

const lineItemRequiresDocGen = (
	original: LineItem,
	edited: LineItem,
): boolean =>
	original.handlingUnit !== edited.handlingUnit ||
	original.unit !== edited.unit ||
	original.totalWeight !== edited.totalWeight ||
	original.length !== edited.length ||
	original.width !== edited.width ||
	original.height !== edited.height ||
	original.pieceCount !== edited.pieceCount ||
	original.stackable !== edited.stackable ||
	original.hazmat !== edited.hazmat ||
	original.commodityDescription !== edited.commodityDescription ||
	original.class !== edited.class ||
	original.nmfc !== edited.nmfc;

const freightRequiresDocGen = (
	original: FreightDetails | undefined,
	edited: FreightDetails | undefined,
): boolean => {
	if (!original || !edited) {
		return false;
	}
	const originalItems = original.lineItems ?? [];
	const editedItems = edited.lineItems ?? [];
	if (originalItems.length !== editedItems.length) {
		return true;
	}
	return originalItems.some((item, index) => {
		const editedItem = editedItems[index];
		return editedItem ? lineItemRequiresDocGen(item, editedItem) : true;
	});
};

const additionalDetailsRequireDocGen = (
	original: AdditionalDetails | undefined,
	edited: AdditionalDetails | undefined,
): boolean =>
	original?.emergencyContactName !== edited?.emergencyContactName ||
	original?.emergencyContactNumber !== edited?.emergencyContactNumber ||
	original?.customerReference !== edited?.customerReference ||
	original?.soNumber !== edited?.soNumber ||
	original?.poNumber !== edited?.poNumber;

/**
 * Determines whether the edits made to a shipment require document generation,
 * based on the "Cause Document Updates?" column of the shipment edit matrix.
 */
export const determineDocGenRequired = (
	original: ShipmentDetailsData | undefined,
	edited: ShipmentDetailsData | undefined,
	originalShipmentAccessorials?: readonly string[] | null,
	editedShipmentAccessorials?: readonly string[] | null,
	originalCustomer?: ShipmentCustomer | null,
	editedCustomer?: ShipmentCustomer | null,
	rerateRequired?: boolean,
): boolean => {
	if (!original || !edited) {
		return false;
	}

	return (
		rerateRequired === true ||
		stopRequiresDocGen(original.pickup, edited.pickup, true) ||
		stopRequiresDocGen(original.delivery, edited.delivery, false) ||
		freightRequiresDocGen(original.freightDetails, edited.freightDetails) ||
		additionalDetailsRequireDocGen(
			original.additionalDetails,
			edited.additionalDetails,
		) ||
		accessorialsChanged(
			originalShipmentAccessorials,
			editedShipmentAccessorials,
		) ||
		originalCustomer?.id !== editedCustomer?.id
	);
};

// The pickup being rescheduled past the quote's expiration date requires a
// rerate, but only while the shipment has not yet been picked up (an actual
// pickup time clears this). Needs the quote expiration date, which lives on the
// carrier, so it is passed in.
const pickupScheduledBeyondExpiration = (
	original: Stop | undefined,
	edited: Stop | undefined,
	quoteExpirationDate?: string | null,
): boolean => {
	if (original?.actual) {
		return false;
	}
	if (original?.scheduled === edited?.scheduled) {
		return false;
	}
	const expiration = parseDate(quoteExpirationDate);
	const scheduled = parseDate(edited?.scheduled);
	if (!expiration || !scheduled) {
		return false;
	}
	return isAfter(scheduled, expiration);
};

/**
 * Determines whether the edits made to a shipment require a rerate, based on the
 * "Cause Rerate?" column of the shipment edit matrix.
 *
 * Covered: pickup/delivery company name, address, location type and
 * accessorials; commodity handling unit, weight, dimensions, stackable, hazmat,
 * description, class and NMFC; commodity piece count when "Sort and Segregate"
 * is selected; the freight-level overwrite calculated linear feet value; and the
 * pickup being rescheduled past the quote expiration date while the shipment
 * has not yet been picked up.
 *
 * @param quoteExpirationDate The quote expiration date (carrier `expDate`).
 */
export const determineRerateRequired = (
	original: ShipmentDetailsData | undefined,
	edited: ShipmentDetailsData | undefined,
	quoteExpirationDate?: string | null,
	originalShipmentAccessorials?: readonly string[] | null,
	editedShipmentAccessorials?: readonly string[] | null,
	originalCustomer?: ShipmentCustomer | null,
	editedCustomer?: ShipmentCustomer | null,
): boolean => {
	if (!original || !edited) {
		return false;
	}

	const includePieceCount =
		sortAndSegregateSelected(edited) || sortAndSegregateSelected(original);

	return (
		stopRequiresRerate(original.pickup, edited.pickup) ||
		stopRequiresRerate(original.delivery, edited.delivery) ||
		customerRequiresRerate(originalCustomer, editedCustomer) ||
		accessorialsChanged(
			originalShipmentAccessorials,
			editedShipmentAccessorials,
		) ||
		freightDetailsRequiresRerate(
			original.freightDetails,
			edited.freightDetails,
			includePieceCount,
		) ||
		pickupScheduledBeyondExpiration(
			original.pickup,
			edited.pickup,
			quoteExpirationDate,
		)
	);
};
